import { Fragment, ReactElement, ReactNode, useState } from 'react';
import { formatNumber } from './breakdownFormat';

export interface AdjustmentRow {
  label: string;
  description: string;
  value: number;
  available: boolean;
}

interface Props {
  cdaRef: number;
  cdaRefLabel?: string;
  cdaAdjusted: number;
  airspeedAdjustment: AdjustmentRow;
  yawAdjustment: AdjustmentRow;
  chestAdjustment: AdjustmentRow;
  headAdjustment: AdjustmentRow;
  contourDetail: ReactNode;
  bodyContourDetail: ReactNode;
  rawCrrDetail: ReactNode;
  crrAt20C: number;
}

function ToggleButton({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
      className={
        'cr-w-6 cr-h-6 cr-flex cr-items-center cr-justify-center cr-rounded ' +
        'cr-border cr-border-gray-300 cr-bg-white cr-text-gray-700 cr-text-sm cr-font-bold ' +
        'hover:cr-bg-blue-50 hover:cr-border-blue-500 hover:cr-text-blue-700'
      }
    >
      {isExpanded ? '−' : '+'}
    </button>
  );
}

function AdjustmentTr({ row }: { row: AdjustmentRow }): ReactElement {
  return (
    <tr className="cr-border-b cr-border-gray-100">
      <td className="cr-py-2"></td>
      <td className="cr-py-2 cr-pr-4 cr-font-medium">{row.label}</td>
      <td className="cr-py-2 cr-pr-4 cr-text-gray-500">{row.description}</td>
      <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">
        {row.available ? formatNumber(row.value, 4) : '—'}
      </td>
    </tr>
  );
}

export default function CrrSummary({
  cdaRef,
  cdaRefLabel = 'config.cda_ref',
  cdaAdjusted,
  airspeedAdjustment,
  yawAdjustment,
  chestAdjustment,
  headAdjustment,
  contourDetail,
  bodyContourDetail,
  rawCrrDetail,
  crrAt20C,
}: Props): ReactElement {
  const [contourExpanded, setContourExpanded] = useState(false);
  const [bodyContourExpanded, setBodyContourExpanded] = useState(false);
  const [rawCrrExpanded, setRawCrrExpanded] = useState(false);

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-mb-6">
      <h3 className="font-montserrat cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-3">
        Crr Calculation
      </h3>
      <table className="cr-w-full cr-text-sm">
        <thead>
          <tr className="cr-border-b cr-border-gray-300 cr-text-gray-700">
            <th className="cr-w-8"></th>
            <th className="cr-text-left cr-py-2 cr-pr-4">Component</th>
            <th className="cr-text-left cr-py-2 cr-pr-4">Description</th>
            <th className="cr-text-right cr-py-2 cr-pr-4">Value (m²)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cr-border-b cr-border-gray-100">
            <td className="cr-py-2"></td>
            <td className="cr-py-2 cr-pr-4 cr-font-medium">Reference CdA</td>
            <td className="cr-py-2 cr-pr-4 cr-text-gray-500">
              session-level baseline (<code>{cdaRefLabel}</code>)
            </td>
            <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">
              {Number.isFinite(cdaRef) ? formatNumber(cdaRef, 4) : '—'}
            </td>
          </tr>
          <AdjustmentTr row={airspeedAdjustment} />
          <AdjustmentTr row={yawAdjustment} />
          <Fragment>
            <tr className="cr-border-b cr-border-gray-100 cr-bg-gray-50">
              <td className="cr-py-2 cr-text-center">
                <ToggleButton
                  isExpanded={contourExpanded}
                  onToggle={() => setContourExpanded((v) => !v)}
                />
              </td>
              <td colSpan={3} className="cr-py-2 cr-pr-4 cr-text-gray-700 cr-italic">
                Airspeed / Yaw Normalisation Detail
              </td>
            </tr>
            {contourExpanded && (
              <tr>
                <td colSpan={4} className="cr-bg-gray-50 cr-px-4 cr-py-3 cr-border-b cr-border-gray-100">
                  {contourDetail}
                </td>
              </tr>
            )}
          </Fragment>
          <AdjustmentTr row={chestAdjustment} />
          <AdjustmentTr row={headAdjustment} />
          <Fragment>
            <tr className="cr-border-b cr-border-gray-100 cr-bg-gray-50">
              <td className="cr-py-2 cr-text-center">
                <ToggleButton
                  isExpanded={bodyContourExpanded}
                  onToggle={() => setBodyContourExpanded((v) => !v)}
                />
              </td>
              <td colSpan={3} className="cr-py-2 cr-pr-4 cr-text-gray-700 cr-italic">
                Body Normalisation Detail
              </td>
            </tr>
            {bodyContourExpanded && (
              <tr>
                <td colSpan={4} className="cr-bg-gray-50 cr-px-4 cr-py-3 cr-border-b cr-border-gray-100">
                  {bodyContourDetail}
                </td>
              </tr>
            )}
          </Fragment>
          <tr>
            <td colSpan={4} className="cr-py-0">
              <div className="cr-border-t-2 cr-border-blue-900 cr-mt-1" />
            </td>
          </tr>
          <tr className="cr-bg-blue-50">
            <td className="cr-py-2"></td>
            <td className="cr-py-2 cr-pr-4 cr-font-semibold cr-text-blue-900">Adjusted CdA</td>
            <td className="cr-py-2 cr-pr-4 cr-text-gray-600 cr-italic">
              cda_ref + (airspeed + yaw + chest + head adjustments for this traverse)
            </td>
            <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold cr-text-blue-900">
              {Number.isFinite(cdaAdjusted) ? formatNumber(cdaAdjusted, 4) : '—'}
            </td>
          </tr>
          <Fragment>
            <tr className="cr-border-b cr-border-gray-100 cr-bg-gray-50">
              <td className="cr-py-2 cr-text-center">
                <ToggleButton
                  isExpanded={rawCrrExpanded}
                  onToggle={() => setRawCrrExpanded((v) => !v)}
                />
              </td>
              <td className="cr-py-2 cr-pr-4 cr-font-medium">Raw Crr</td>
              <td className="cr-py-2 cr-pr-4 cr-text-gray-600 cr-italic">
                energy balance solved for rolling resistance, then temperature-normalised
              </td>
              <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold cr-text-blue-900">
                {Number.isFinite(crrAt20C) ? formatNumber(crrAt20C, 5) : '—'}
              </td>
            </tr>
            {rawCrrExpanded && (
              <tr>
                <td colSpan={4} className="cr-bg-gray-50 cr-px-4 cr-py-3 cr-border-b cr-border-gray-100">
                  {rawCrrDetail}
                </td>
              </tr>
            )}
          </Fragment>
        </tbody>
      </table>
      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Adjustments use this traverse&rsquo;s pipeline-computed airspeed / yaw / chest / head
        values (matching the data table). The Adjusted CdA, Aero, Rolling Power, and Crr
        therefore vary per traverse in step with the actual run conditions.
      </p>
    </div>
  );
}
